/**
 * Features Service
 *
 * Fetches environment-level feature flags from the backend.
 * The endpoint is anonymous (no auth required) so it can be called
 * before authentication completes.
 *
 * IMPORTANT: Uses fetch() directly instead of apiClient to avoid
 * the apiClient request interceptor, which calls getValidToken() →
 * refreshToken() → acquireTokenSilent(). During the MSAL redirect
 * callback (before handleRedirectPromise() completes), there is no
 * active MSAL account yet, so acquireTokenSilent() fails and triggers
 * the auth expiration callback, which navigates away from /auth/callback
 * and destroys the in-flight token exchange.
 */

/**
 * Shape of the features response from GET /api/features.
 */
export interface FeaturesConfig {
  /** Whether the flower catalog feature is enabled in this environment. */
  flowerCatalog: boolean;
  /** True only when flower catalog is enabled AND at least one active product exists. */
  flowerCatalogHasProducts: boolean;
  /** Whether the mailed-card catalog feature is enabled in this environment. */
  mailedCardCatalog: boolean;
  /** True only when mailed-card catalog is enabled AND writable artwork exists. */
  mailedCardCatalogHasArtworks: boolean;
  /**
   * When true, My Account shows how assistant orders are approved.
   * Spending limits stay visible either way.
   */
  agentOrdersEnabled: boolean;
  /**
   * Public /ai page and AI nav link. False in production until assistant
   * directory listings are registered (Features:PublicAiPage).
   */
  publicAiPage: boolean;
  /** True when the Goody gift catalog stage is Active (customer shop). */
  giftCatalog: boolean;
}

/** All-false defaults used when the endpoint is unreachable. */
const DEFAULTS: FeaturesConfig = {
  flowerCatalog: false,
  flowerCatalogHasProducts: false,
  mailedCardCatalog: false,
  mailedCardCatalogHasArtworks: false,
  agentOrdersEnabled: false,
  publicAiPage: false,
  giftCatalog: false,
};

/**
 * Fetches the current feature flags from the backend.
 * On error, returns all-false defaults so the app never fails to mount.
 */
export const getFeatures = async (): Promise<FeaturesConfig> => {
  try {
    const baseUrl = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(`${baseUrl}/features`);

    if (!response.ok) {
      return DEFAULTS;
    }

    return await response.json();
  } catch {
    return DEFAULTS;
  }
};
